import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Check, CheckCheck, CheckCircle2, ChevronLeft, Search, Phone, Video, Send, Signal, Wifi, BatteryFull, Loader2, Lock, LockOpen, Camera } from "lucide-react";
import { useT } from "../i18n";
import D from "../content/demos";
import P from "../content/phone";
import { Plate } from "./kit";
import { Mark } from "./Brand";
import { Call } from "./Demo";

const ICON = "h-4 w-4 stroke-[1.75]";
const PLATE = "AB 123 CD";

// Plays a scenario once when it scrolls into view (-1 = idle, 0..n = stages). Prerender and reduced motion show the final state.
const usePlay = (ref, delays) => {
  const reduce = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const total = delays.length;
  const [st, setSt] = useState(total);
  useEffect(() => {
    if (reduce) { setSt(total); return undefined; }
    if (!inView) { setSt(-1); return undefined; }
    let i = 0;
    let t;
    setSt(0);
    const next = () => { t = setTimeout(() => { i += 1; setSt(i); if (i < total) next(); }, delays[i]); };
    next();
    return () => clearTimeout(t);
  }, [inView, reduce]); // eslint-disable-line react-hooks/exhaustive-deps
  return { st, run: inView && !reduce };
};

// Types `text` while stage === at, shows it in full once stage > at.
const Type = ({ text, st, at, speed = 70, className = "" }) => {
  const typing = st === at;
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!typing) return undefined;
    setN(0);
    const id = setInterval(() => setN((v) => Math.min(text.length, v + 1)), speed);
    return () => clearInterval(id);
  }, [typing, text, speed]);
  const shown = st > at ? text : typing ? text.slice(0, n) : "";
  return <span className={`font-plate ${className}`}>{shown}{typing && <span className="animate-pulse">|</span>}</span>;
};
const typeMs = (text, speed = 70) => text.length * speed + 450;

const Field = ({ label, active, children }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.12em] text-white/60">{label}</p>
    <div className={`mt-1 h-9 rounded-lg border px-3 py-2 text-[13px] transition-colors duration-300 ${active ? "border-[#5d68ff] bg-[#2E3CFD]/10" : "border-white/15 bg-white/5"}`}>{children}</div>
  </div>
);

const Btn = ({ pressed, done, children, testid }) => (
  <motion.div data-testid={testid} animate={{ scale: pressed && !done ? 0.96 : 1 }} transition={{ duration: 0.2 }} className={`flex items-center justify-center gap-2 rounded-full py-2.5 text-[13px] font-semibold transition-colors duration-300 ${done ? "bg-[#1f9d61]" : "bg-[#2E3CFD]"}`}>
    {pressed && !done && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
    {done && <Check className="h-4 w-4 stroke-[2.5]" aria-hidden />}
    {children}
  </motion.div>
);

const Pop = ({ show, children, className = "", y = 10 }) => (
  <motion.div initial={false} animate={{ opacity: show ? 1 : 0, y: show ? 0 : y }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} className={className} aria-hidden={!show}>{children}</motion.div>
);

const ChatTop = ({ d }) => (
  <div className="flex items-center gap-2 border-b border-white/10 px-3 pb-3">
    <ChevronLeft className="h-5 w-5 text-white/80" aria-hidden />
    <Plate text={PLATE} size="sm" />
    <span className="ml-auto flex gap-3 text-white/80"><Phone className={ICON} aria-label={d.audioCall} /><Video className={ICON} aria-label={d.videoCall} /></span>
  </div>
);

const Sent = ({ text, ticks, p, photo }) => (
  <div className="flex flex-col items-end">
    <div className={`rounded-2xl rounded-br-sm bg-[#2E3CFD] text-[13px] leading-snug text-white ${photo ? "w-[78%] p-1" : "max-w-[84%] px-3 py-2"}`}>
      {photo && <img src={photo} alt="" className="aspect-[4/3] w-full rounded-xl object-cover" data-testid="phone-sent-photo" />}
      <p className={photo ? "px-2 pt-1.5" : ""}>{text}</p>
      <p className={`mt-1 flex items-center justify-end gap-1 text-[10px] text-white/75 ${photo ? "px-2 pb-1" : ""}`}>9:41{ticks > 1 ? <CheckCheck className="h-3.5 w-3.5 text-[#9fe8c0]" aria-hidden /> : <Check className="h-3.5 w-3.5" aria-hidden />}</p>
    </div>
    {ticks > 1 && <p className="mt-1 text-[10px] text-white/60">{p.read}</p>}
  </div>
);

const Composer = ({ children, d, active, cam, attach }) => (
  <div className="px-3 pb-2">
    {attach && (
      <motion.div initial={{ opacity: 0, y: 12, scale: 0.8 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.35 }} className="relative mb-2 ml-10 h-16 w-20 overflow-hidden rounded-xl border-2 border-white/80" data-testid="phone-attach-preview">
        <img src={attach} alt="" className="h-full w-full object-cover" />
      </motion.div>
    )}
    <div className="flex items-center gap-2">
      {cam !== undefined && (
        <motion.span animate={{ scale: cam ? [1, 0.85, 1] : 1 }} transition={{ duration: 0.4 }} className={`grid h-8 w-8 shrink-0 place-items-center rounded-full transition-colors ${cam ? "bg-[#F5C842] text-[#171940]" : "bg-white/10 text-white/80"}`}><Camera className="h-4 w-4" aria-hidden /></motion.span>
      )}
      <div className={`min-h-[34px] flex-1 rounded-2xl border px-3 py-2 text-[12px] leading-snug transition-colors ${active ? "border-[#5d68ff]" : "border-white/25"}`}>{children || <span className="text-white/50">{d.typeMsg}</span>}</div>
      <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full transition-colors ${active ? "bg-[#2E3CFD]" : "bg-white/10"}`}><Send className="h-3.5 w-3.5" aria-hidden /></span>
    </div>
  </div>
);

const RegisterScreen = ({ st, p }) => {
  const vals = ["Alex", "Morgan", "alex@example.com", "+1 555 0100", "••••••••"];
  const labels = [p.name, p.surname, p.email, p.phone, p.password];
  return (
    <div className="flex h-full flex-col px-4">
      <p className="font-display text-lg font-bold">{p.create}</p>
      <p className="mt-1 text-[11px] text-white/70">{p.step.replace("{n}", "1")} · {p.personal}</p>
      <div className="mt-2 flex gap-1">{[0, 1, 2].map((i) => <span key={i} className={`h-1 flex-1 rounded-full ${i === 0 ? "bg-[#5d68ff]" : "bg-white/15"}`} />)}</div>
      <div className="mt-4 space-y-2.5">
        {labels.map((l, i) => <Field key={l} label={l} active={st === i}><Type text={vals[i]} st={st} at={i} speed={60} /></Field>)}
      </div>
      <div className="mt-auto pb-3"><Btn pressed={st >= 5} done={st >= 6} testid="phone-register-continue">{p.next}</Btn></div>
    </div>
  );
};

const PlateScreen = ({ st, p }) => (
  <div className="flex h-full flex-col px-4">
    <p className="font-display text-lg font-bold">{p.vehicle}</p>
    <div className="mt-5"><Field label={p.plate} active={st === 0}><Type text={PLATE} st={st} at={0} speed={110} className="font-semibold tracking-[0.08em]" /></Field></div>
    <p className="mt-2 text-[11px] text-white/70">{p.plateHint}</p>
    <Pop show={st >= 1} className="mt-6 grid place-items-center rounded-2xl bg-white py-6 text-[#171940]">
      <Plate text={PLATE} size="lg" />
    </Pop>
    <Pop show={st >= 3} className="mt-4 flex items-center gap-2 rounded-xl border border-[#1f9d61]/50 bg-[#1f9d61]/15 px-3 py-2.5 text-[12px] text-[#9fe8c0]">
      <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />{p.saved}
    </Pop>
    <div className="mt-auto pb-3"><Btn pressed={st >= 2} done={st >= 3} testid="phone-plate-save">{p.save}</Btn></div>
  </div>
);

const SearchScreen = ({ st, d }) => (
  <div className="flex h-full flex-col px-4">
    <span className="flex items-center gap-1.5"><Mark className="h-5 w-5" /><span className="font-display text-sm font-bold tracking-tight">chatplate</span></span>
    <p className="mt-5 font-display text-[17px] font-bold leading-tight">{d.homeTitle}</p>
    <div className={`mt-4 flex h-11 items-center justify-between rounded-xl border bg-[#0d1543] px-3 text-[13px] ${st === 0 ? "border-[#5d68ff]" : "border-[#2E3CFD]/60"}`}>
      {st < 0 ? <span className="text-white/50">{d.typePh}</span> : <Type text={PLATE} st={st} at={0} speed={110} className="font-semibold tracking-[0.08em]" />}
      <Search className={`${ICON} text-[#F5C842]`} aria-hidden />
    </div>
    <p className="mt-2 text-[11px] text-white/70">{d.hint}</p>
    <Pop show={st >= 1} className="mt-4 rounded-xl bg-white px-3 py-3 text-[#171940]">
      <Plate text={PLATE} size="sm" />
      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="flex min-w-0 items-center gap-1 text-[11px] text-[#171940]/70"><CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[#1f9d61]" aria-hidden />{d.registered}</p>
        <motion.span animate={{ scale: st >= 2 ? [1, 0.9, 1] : 1 }} transition={{ duration: 0.4 }} className={`shrink-0 rounded-full px-3 py-1 text-xs text-white ${st >= 2 ? "bg-[#1f9d61]" : "bg-[#2E3CFD]"}`}>{d.open}</motion.span>
      </div>
    </Pop>
  </div>
);

// Stages: 0 camera tapped, 1 photo attached, 2 typing, 3 sent (photo + text), 4 read.
const PHOTO = "/images/uc-blocked.webp";
const SendScreen = ({ st, d, p, text }) => (
  <div className="flex h-full flex-col">
    <ChatTop d={d} />
    <div className="flex flex-1 flex-col justify-end gap-2 p-3">
      <Pop show={st >= 3}><Sent text={text} photo={PHOTO} ticks={st >= 4 ? 2 : 1} p={p} /></Pop>
    </div>
    <Composer d={d} active={st === 2} cam={st === 0} attach={st >= 1 && st < 3 ? PHOTO : null}>{st === 2 ? <Type text={text} st={st} at={2} speed={35} /> : null}</Composer>
  </div>
);

const pick = (chat, who) => chat.find((m) => m[0] === who)?.[1] ?? "";
const EASE = [0.32, 0.72, 0, 1];

// Stages: 0 locked, 1 notification arrives, 2 notification tapped, 3 unlock -> chat, 4 typing reply, 5 reply sent.
const ReceiveScreen = ({ st, d, p, chat }) => {
  const reply = pick(chat, "me");
  return (
    <motion.div initial={false} animate={{ scale: st >= 3 ? 1 : 0.92, opacity: st >= 3 ? 1 : 0.4 }} transition={{ duration: 0.6, ease: EASE }} className="flex h-full flex-col">
      <ChatTop d={d} />
      <div className="flex flex-1 flex-col justify-end gap-2 p-3">
        <div className="flex justify-start"><div className="max-w-[84%] rounded-2xl rounded-bl-sm bg-white px-3 py-2 text-[13px] leading-snug text-[#171940]"><p>{pick(chat, "them")}</p><p className="mt-1 text-right text-[10px] text-[#171940]/60">9:41</p></div></div>
        <Pop show={st >= 5}><Sent text={reply} ticks={2} p={p} /></Pop>
      </div>
      <Composer d={d} active={st === 4}>{st === 4 ? <Type text={reply} st={st} at={4} speed={45} /> : null}</Composer>
    </motion.div>
  );
};

// Full-screen lock screen that sits above the chat and slides up after the notification is tapped.
const LockScreen = ({ st, p, chat }) => {
  const open = st >= 3;
  const tapped = st >= 2;
  const L = tapped ? LockOpen : Lock;
  return (
    <motion.div initial={false} animate={{ y: open ? "-100%" : "0%" }} transition={{ duration: 0.6, ease: EASE }} className="absolute inset-0 z-10 flex flex-col bg-[radial-gradient(120%_70%_at_15%_0%,#3b47ff_0%,#1b1f6e_45%,#07081a_100%)]" aria-hidden={open} data-testid="phone-lock-screen">
      <Status />
      <div className="mt-7 flex flex-col items-center">
        <L className="h-4 w-4 text-white/85" aria-hidden />
        <p className="mt-2 font-display text-6xl font-semibold tracking-tight">9:41</p>
      </div>
      <div className="relative mt-8 px-3">
        <Pop show={st >= 1} y={-28}>
          <motion.div animate={{ scale: tapped && !open ? 0.95 : 1 }} transition={{ duration: 0.18 }} className={`relative overflow-hidden rounded-2xl p-3 backdrop-blur-md transition-colors duration-200 ${tapped ? "bg-white/30" : "bg-white/15"}`} data-testid="phone-lock-notification">
            <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-white/80"><Mark className="h-4 w-4" />ChatPlate<span className="ml-auto normal-case tracking-normal">{p.now}</span></p>
            <p className="mt-1.5 text-[13px] font-semibold">{p.newMsg} · {PLATE}</p>
            <p className="mt-0.5 text-[12px] leading-snug text-white/85">{pick(chat, "them")}</p>
            {tapped && !open && <motion.span initial={{ scale: 0, opacity: 0.6 }} animate={{ scale: 3, opacity: 0 }} transition={{ duration: 0.6 }} className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" aria-hidden />}
          </motion.div>
        </Pop>
      </div>
      <span className="mx-auto mb-2 mt-auto h-1 w-1/3 rounded-full bg-white/80" aria-hidden />
    </motion.div>
  );
};

const delaysFor = (k, d, chat) => {
  const msg = d.search[0][1];
  const reply = chat.find((m) => m[0] === "me")?.[1] ?? "";
  switch (k) {
    case "register": return [typeMs("Alex", 60), typeMs("Morgan", 60), typeMs("alex@example.com", 60), typeMs("+1 555 0100", 60), typeMs("••••••••", 60), 1100];
    case "plate": return [typeMs(PLATE, 110), 900, 1000];
    case "search": return [typeMs(PLATE, 110), 1100, 800];
    case "send": return [800, 900, typeMs(msg, 35), 1100];
    case "receive": return [800, 1700, 650, 1000, typeMs(reply, 45)];
    default: return [];
  }
};

const Status = () => (
  <div className="flex items-center justify-between px-6 pt-3 text-[11px] font-semibold">
    <span>9:41</span>
    <span className="flex items-center gap-1"><Signal className="h-3 w-3" aria-hidden /><Wifi className="h-3 w-3" aria-hidden /><BatteryFull className="h-3.5 w-3.5" aria-hidden /></span>
  </div>
);

// CSS iPhone with Dynamic Island that plays one "How it works" step once in view.
export const StepPhone = ({ scenario, chat = [], testid }) => {
  const { lang, t } = useT();
  const d = D[lang] || D.en;
  const p = P[lang] || P.en;
  const ref = useRef(null);
  const { st, run } = usePlay(ref, delaysFor(scenario, d, chat));
  const screens = {
    register: <RegisterScreen st={st} p={p} />,
    plate: <PlateScreen st={st} p={p} />,
    search: <SearchScreen st={st} d={d} />,
    send: <SendScreen st={st} d={d} p={p} text={d.search[0][1]} />,
    receive: <ReceiveScreen st={st} d={d} p={p} chat={chat} />,
  };
  const isCall = scenario === "call";
  return (
    <figure ref={ref} className="mx-auto w-full max-w-[270px]" data-testid={testid} data-stage={st}>
      <div className="relative rounded-[3rem] bg-gradient-to-b from-[#d4d7e6] to-[#9da2bf] p-[3px] shadow-[0_50px_90px_-45px_rgba(23,25,64,0.75)] dark:from-[#45498a] dark:to-[#1d1f4a]">
        <span className="absolute -left-[3px] top-28 h-10 w-[3px] rounded-l bg-[#9da2bf] dark:bg-[#2c2f66]" aria-hidden />
        <span className="absolute -left-[3px] top-40 h-14 w-[3px] rounded-l bg-[#9da2bf] dark:bg-[#2c2f66]" aria-hidden />
        <span className="absolute -right-[3px] top-36 h-16 w-[3px] rounded-r bg-[#9da2bf] dark:bg-[#2c2f66]" aria-hidden />
        <div className="rounded-[2.85rem] bg-[#07081a] p-[9px]">
          <div className="relative aspect-[9/19.5] overflow-hidden rounded-[2.3rem] bg-[#171940] text-white">
            <span className="absolute left-1/2 top-2.5 z-20 h-[24px] w-[32%] -translate-x-1/2 rounded-full bg-black" aria-hidden />
            {isCall ? (
              <div className="absolute inset-0 pt-6"><Call d={d} run={run} plate={PLATE} /></div>
            ) : (
              <div className="absolute inset-0 flex flex-col">
                <Status />
                <div className="mt-5 min-h-0 flex-1">{screens[scenario]}</div>
                <span className="mx-auto mb-2 mt-1 h-1 w-1/3 shrink-0 rounded-full bg-white/70" aria-hidden />
              </div>
            )}
            {scenario === "receive" && <LockScreen st={st} p={p} chat={chat} />}
          </div>
        </div>
      </div>
      <figcaption className="mt-3 text-center text-[11px] uppercase tracking-wider text-muted-foreground">{t.common.illustration}</figcaption>
    </figure>
  );
};
